import React from 'react';
import { Paper, Box, Typography, useTheme } from '@mui/material';

/**
 * Section Component
 * A consistent section container with title and optional actions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Section content
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.subtitle - Section subtitle
 * @param {React.ReactNode} props.titleIcon - Icon to display before the title
 * @param {React.ReactNode} props.actions - Actions to display in the header
 * @param {Object} props.sx - Additional styles
 * @param {boolean} props.elevation - Paper elevation level
 * @param {boolean} props.variant - Paper variant
 * @returns {React.ReactElement} Section container component
 */
const Section = ({
  children,
  title,
  subtitle,
  titleIcon,
  actions,
  sx = {},
  elevation = 1,
  variant = 'outlined',
  ...rest
}) => {
  const theme = useTheme();
  
  return (
    <Paper 
      elevation={elevation} 
      variant={variant}
      sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx 
      }}
      {...rest}
    >
      {/* Section Header */}
      {(title || actions) && (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between"
          mb={subtitle ? 1 : 2}
          pb={title ? 1.5 : 0}
          borderBottom={title && !(variant === 'elevation' && elevation === 0) ? `1px solid ${theme.palette.divider}` : 'none'}
        >
          {/* Title with optional icon */}
          {title && (
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                {titleIcon && (
                  <Box display="flex" alignItems="center" color="primary.main">
                    {titleIcon}
                  </Box>
                )}
                <Typography variant="h6" component="h2" fontWeight="medium">
                  {title}
                </Typography>
              </Box>
              
              {/* Subtitle */}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Actions */}
          {actions && (
            <Box display="flex" alignItems="center" gap={1}>
              {actions}
            </Box>
          )}
        </Box>
      )}
      
      {/* Section Content */}
      <Box sx={{ flexGrow: 1 }}>
        {children}
      </Box>
    </Paper>
  );
};

export default Section;